/**
 * Adapted from PlayCanvas Engine's gsplat-resource-cleanup.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'

/** GSplatResourceCleanup 处理的最小内部契约。 */
export interface GSplatDeferredResource {
  readonly refCount: number
  readonly canFinalizeDestroy: boolean
  readonly isDestroyed: boolean
  finalizeDeferredDestroy(): void
}

/** adapter/device 维度的 Resource 延迟销毁队列。 */
export class GSplatResourceCleanup {
  private static readonly cache = new WeakMap<GSplatGraphicsAdapter, GSplatResourceCleanup>()

  private readonly pendingDestroy = new Set<GSplatDeferredResource>()

  static queueDestroy(adapter: GSplatGraphicsAdapter, resource: GSplatDeferredResource): void {
    GSplatResourceCleanup.forAdapter(adapter).pendingDestroy.add(resource)
  }

  static process(adapter: GSplatGraphicsAdapter): void {
    const cleanup = GSplatResourceCleanup.cache.get(adapter)
    if (!cleanup) return
    cleanup.processPending()
  }

  /**
   * adapter/device 已丢失时 GPU owner 会自行失效；这里只断开 pending 的 CPU 强引用。
   */
  static destroyAdapter(adapter: GSplatGraphicsAdapter): void {
    const cleanup = GSplatResourceCleanup.cache.get(adapter)
    cleanup?.destroy()
    GSplatResourceCleanup.cache.delete(adapter)
  }

  static getPendingCount(adapter: GSplatGraphicsAdapter): number {
    return GSplatResourceCleanup.cache.get(adapter)?.pendingDestroy.size ?? 0
  }

  private static forAdapter(adapter: GSplatGraphicsAdapter): GSplatResourceCleanup {
    let cleanup = GSplatResourceCleanup.cache.get(adapter)
    if (!cleanup) {
      cleanup = new GSplatResourceCleanup()
      GSplatResourceCleanup.cache.set(adapter, cleanup)
    }
    return cleanup
  }

  private processPending(): void {
    for (const resource of this.pendingDestroy) {
      if (!resource.canFinalizeDestroy) continue
      try {
        resource.finalizeDeferredDestroy()
      } finally {
        if (resource.isDestroyed) this.pendingDestroy.delete(resource)
      }
    }
  }

  private destroy(): void {
    this.pendingDestroy.clear()
  }
}
